import { z } from "zod";
import {
  CustomerSchema,
  LeadCustomerSchema,
  SuccessMetaSchema,
} from "../common";

// =========================================================================
// POST /orders - REQUEST
// =========================================================================

// Shipping section
export const ShippingSchema = z.object({
  title: z.string().optional(),
  rule_id: z.string().min(1, "Shipping rule ID required"),
  cost: z.number().nonnegative(),
});

// Pricing section
export const PricingSchema = z.object({
  subtotal: z.number().nonnegative(),
  total: z.number().nonnegative(),
  currency: z.enum(["USD", "COP", "MXN", "PEN", "ARS"]),
});

// Attribution metadata
export const AttributionSchema = z.object({
  utm_source: z.string().optional(),
  utm_campaign: z.string().optional(),
  utm_medium: z.string().optional(),
  fbp: z.string().optional(),
  fbc: z.string().optional(),
  user_agent: z.string().optional(),
  ip_address: z.string().optional(),
  /**
   * Tracking event_id minted at bootstrap (evt_<uuid>). The widget fires the
   * client-side Purchase pixel with this id and forwards it here so the
   * server-side Purchase (Meta CAPI / TikTok Events API) can send the SAME id
   * for cross-side deduplication. Optional — absent for non-widget callers.
   */
  event_id: z.string().optional(),
});

export const OrderLineItemSchema = z
  .object({
    variant_id: z.string().min(1).optional(),
    product_id: z.string().min(1).optional(),
    quantity: z.number().int().positive(),
    properties: z.record(z.string(), z.any()).optional(),
    from_offer_id: z.string().min(1).optional(),
    is_main_item: z.boolean().optional(),
  })
  .refine(
    (data) => {
      const isShopifyLine = !!data.variant_id && !!data.product_id;
      const isSyntheticLine =
        !data.variant_id && !data.product_id && !!data.from_offer_id;
      return isShopifyLine || isSyntheticLine;
    },
    {
      message:
        "Cada item debe ser una línea de Shopify (variant_id + product_id) o una línea sintética (sólo from_offer_id)",
      path: ["variant_id"],
    }
  );

export const FinancialLineItemSchema = z.object({
  variant_id: z.string(),
  product_id: z.string().optional(),
  quantity: z.number(),

  from_offer_id: z.string().optional(),

  base_price: z.number(),
  final_unit_price: z.number(),
  line_total: z.number(),
  discount_amount: z.number(),

  pricing_source: z.enum(["catalog", "discount", "synthetic"]),
});

// Campos comunes a todos los pedidos
const BaseCreateOrderRequestSchema = z.object({
  shop_domain: z
    .string()
    .min(1)
    .regex(/\.myshopify\.com$/, "Invalid Shopify domain"),
  form_id: z.string().min(1),
  form_variant_id: z.string().optional(),
  items: z.array(OrderLineItemSchema).min(1, "At least one item required"),
  shipping: ShippingSchema,
  pricing: PricingSchema,
  attribution: AttributionSchema.optional(),
  custom_fields: z.record(z.string(), z.string()).optional(),
  accepted_downsell_offer_id: z.string().optional(),
});

// Lead branch is tried first: source must be "lead" (explicit).
// Direct branch: source is optional for backward compat with callers that omit it.
export const CreateOrderRequestSchema = z.union([
  BaseCreateOrderRequestSchema.extend({
    source: z.literal("lead"),
    customer: LeadCustomerSchema,
  }),
  BaseCreateOrderRequestSchema.extend({
    source: z.enum(["direct"]).optional(),
    customer: CustomerSchema,
  }),
]);

// =========================================================================
// POST /orders - RESPONSE (202 Accepted)
// =========================================================================

export const OrderStatusEnum = z.enum([
  "pending",
  "processing_fraud",
  "fraud_blocked",
  "draft_created",
  "finalizing",
  "completed",
  "failed",
]);

export const CreateOrderResponseSchema = z.object({
  meta: SuccessMetaSchema.extend({
    status: z.literal("accepted"),
  }),
  order: z.object({
    id: z.string(), // UUID interno
    order_number: z.string().regex(/^#\d+$/, "Invalid order number format"),
    status: OrderStatusEnum,
    created_at: z.iso.datetime(),
  }),
  next_steps: z.object({
    poll_url: z.string(),
    upsell_url: z.string(),
    finalize_url: z.string(),
  }),
});

// =========================================================================
// GET /orders/:id/status - RESPONSE
// =========================================================================

export const OrderStatusResponseSchema = z.object({
  status: OrderStatusEnum,
  is_completed: z.boolean(),
  shopify_order_id: z.string().nullable(),
  status_page_url: z.string().url().nullable().optional(),
  error: z
    .object({
      code: z.enum([
        "NO_COVERAGE",
        "PROCESSING_ERROR",
        "SYSTEM_ERROR",
        "GENERIC",
      ]),
      message: z.string().min(1),
    })
    .optional(),
});

// =========================================================================
// POST /orders/:id/upsell - REQUEST
// =========================================================================

export const UpsellItemSchema = z.object({
  variant_id: z.string().min(1),
  quantity: z.number().int().positive().default(1),
});

export const AddUpsellRequestSchema = z.object({
  variant_id: z.string().min(1),
  items: z.array(UpsellItemSchema).min(1),
});

export const AddUpsellResponseSchema = z.object({
  meta: SuccessMetaSchema.extend({
    status: z.literal("accepted"),
  }),
});

// =========================================================================
// POST /orders/:id/finalize - REQUEST & RESPONSE
// =========================================================================

export const FinalizeOrderRequestSchema = z.object({
  client_details: z
    .object({
      upsell_rejected_offer_id: z.string().optional(),
    })
    .optional(),
});

export const FinalizeOrderResponseSchema = z.object({
  meta: SuccessMetaSchema.extend({
    status: z.literal("accepted"),
  }),
  order: z.object({
    id: z.string(),
    status: z.literal("finalizing"),
    is_locked: z.boolean(),
    redirect_url: z.string(),
  }),
});

// =========================================================================
// TYPE EXPORTS (para usar en TypeScript)
// =========================================================================

export type CreateOrderRequest = z.infer<typeof CreateOrderRequestSchema>;
export type CreateOrderResponse = z.infer<typeof CreateOrderResponseSchema>;
export type OrderStatusResponse = z.infer<typeof OrderStatusResponseSchema>;
export type AddUpsellRequest = z.infer<typeof AddUpsellRequestSchema>;
export type AddUpsellResponse = z.infer<typeof AddUpsellResponseSchema>;
export type FinalizeOrderRequest = z.infer<typeof FinalizeOrderRequestSchema>;
export type FinalizeOrderResponse = z.infer<typeof FinalizeOrderResponseSchema>;
export type FinancialLineItem = z.infer<typeof FinancialLineItemSchema>;
